import type { FilterMetadata } from '@kizen/filters/types';

type Condition = ValueOf<ReturnType<typeof getConditions>>;

type Metadata = ReturnType<typeof getMetadata>;

export type ConditionValue = ExtractConditionValue<Condition['ops']>;

type _ConditionValues = {
  [K in keyof Metadata['steps']['condition']['__options_lookup']]: ExtractConditionValue<
    Metadata['steps']['condition']['__options_lookup'][K][number]['ops']
  >;
};

type ExtractConditionValue<T extends Readonly<any[]>> =
  T extends Readonly<[Readonly<[infer K, infer V]>, ...infer Rest]>
    ? K extends 'condition'
      ? V
      : ExtractConditionValue<Rest>
    : never;

const individual_field_option_resource = (t: (x: string) => string) => {
  return {
    method: 'POST',
    url: '/records/display-names',
    body: {
      activity_option_ids: ['xf_concat', ['xf_pick', '$value', ['id']]],
    },
    result_path: [],
    result_transform: [
      'xf_hoist',
      [
        'xf_concat',
        [
          'xf_map',
          '@result_path/not_found/activity_options',
          {
            id: ['xf_pick', '$', ['id']],
            name: ['xf_join', '[Deleted] ', ['xf_pick', '$', ['display_name']]],
            not_found: true,
            error_message: t('The option has been deleted'),
          },
        ],
        [
          'xf_map',
          '@result_path/results/activity_options',
          {
            id: ['xf_pick', '$', ['id']],
            name: ['xf_pick', '$', ['display_name']],
          },
        ],
      ],
    ],
  };
};

const multiple_field_options_resource = (t: (x: string) => string) => {
  return {
    method: 'POST',
    url: '/records/display-names',
    body: {
      activity_option_ids: ['xf_map', '$value', ['xf_pick', '$', ['id']]],
    },
    result_path: [],
    result_transform: [
      'xf_concat',
      [
        'xf_map',
        '@result_path/not_found/activity_options',
        {
          id: ['xf_pick', '$', ['id']],
          name: ['xf_join', '[Deleted] ', ['xf_pick', '$', ['display_name']]],
          not_found: true,
          error_message: t('The option has been deleted'),
        },
      ],
      [
        'xf_map',
        '@result_path/results/activity_options',
        {
          id: ['xf_pick', '$', ['id']],
          name: ['xf_pick', '$', ['display_name']],
        },
      ],
    ],
  };
};

const getConditions = (t: (x: string) => string) => {
  return {
    between: {
      value: 'between',
      label: t('Is Between'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', 'between']],
    },
    contains: {
      value: 'contains',
      label: t('Contains'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', 'contains']],
    },
    does_not_contain: {
      value: 'does_not_contain',
      label: t('Does Not Contain'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', '!contains']],
    },
    does_not_end_with: {
      value: 'does_not_end_with',
      label: t("Doesn't End With"),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', '!ends_with']],
    },
    does_not_start_with: {
      value: 'does_not_start_with',
      label: t("Doesn't Start With"),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', '!starts_with']],
    },
    earlier_than: {
      value: 'earlier_than',
      label: t('Earlier Than'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', '<']],
    },
    earlier_than_or_on: {
      value: 'earlier_than_or_on',
      label: t('Earlier Than Or On'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', '<=']],
    },
    ends_with: {
      value: 'ends_with',
      label: t('Ends With'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', 'ends_with']],
    },
    equals: {
      value: 'equals',
      label: t('Equals'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', '=']],
    },
    does_not_equal: {
      value: 'does_not_equal',
      label: t('Does Not Equal'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', '!=']],
    },
    has: {
      __labels: {
        dynamictags: t('Has Value'),
      },
      value: 'has',
      label: [
        [
          '@__labels',
          [
            '$field_id',
            [['custom_object_field', 'field_type'], ['field_type']],
          ],
        ],
        t('Contains'),
      ],
      next: [{ step: 'value', unset: true }],
      ops: [['condition', 'has_any']],
    },
    has_not: {
      __labels: {
        dynamictags: t("Doesn't Have Value"),
      },
      value: '!has',
      label: [
        [
          '@__labels',
          [
            '$field_id',
            [['custom_object_field', 'field_type'], ['field_type']],
          ],
        ],
        t('Does Not Contain'),
      ],
      next: [{ step: 'value', unset: true }],
      ops: [['condition', '!has_any']],
    },
    has_any: {
      value: 'has_any',
      label: t('Contains Any Of'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', 'has_any']],
    },
    has_not_any: {
      value: '!has_any',
      label: t('Contains None Of'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', '!has_any']],
    },
    is: {
      value: 'contains',
      label: t('Contains'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', 'is_any_of']],
    },
    is_not: {
      value: 'does_not_contain',
      label: t('Does Not Contain'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', 'is_none_of']],
    },
    is_any_of: {
      __labels: {
        relationship: t('Contains Any Of'),
      },
      value: 'is_any_of',
      label: [
        [
          '@__labels',
          [
            '$field_id',
            [['custom_object_field', 'field_type'], ['field_type']],
          ],
        ],
        t('Is Any Of'),
      ],
      next: [{ step: 'value', unset: true }],
      ops: [['condition', 'is_any_of']],
    },
    is_checked: {
      value: 'is_checked',
      label: t('Is Checked'),
      next: ['__END__'],
      ops: [
        ['condition', '='],
        ['value', true],
      ],
    },
    is_none_of: {
      __labels: {
        relationship: t('Contains None Of'),
      },
      value: 'is_none_of',
      label: [
        [
          '@__labels',
          [
            '$field_id',
            [['custom_object_field', 'field_type'], ['field_type']],
          ],
        ],
        t('Is None Of'),
      ],
      next: [{ step: 'value', unset: true }],
      ops: [['condition', 'is_none_of']],
    },
    is_blank: {
      value: 'is_blank',
      label: t('Is Blank'),
      next: ['__END__'],
      ops: [
        ['condition', 'is_blank'],
        ['value', true],
      ],
    },
    is_me: {
      value: 'is_me',
      label: t('Is Me'),
      next: ['__END__'],
      ops: [
        ['condition', '='],
        ['value', 'is_me'],
      ],
    },
    is_not_blank: {
      value: 'is_not_blank',
      label: t("Isn't Blank"),
      next: ['__END__'],
      ops: [
        ['condition', 'is_blank'],
        ['value', false],
      ],
    },
    is_not_checked: {
      value: 'is_not_checked',
      label: t("Isn't Checked"),
      next: ['__END__'],
      ops: [
        ['condition', '='],
        ['value', false],
      ],
    },
    later_than: {
      value: 'later_than',
      label: t('Later Than'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', '>']],
    },
    later_than_or_on: {
      value: 'later_than_or_on',
      label: t('Later Than Or On'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', '>=']],
    },
    less_than: {
      value: 'less_than',
      label: t('Less Than'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', '<']],
    },
    less_than_number: {
      value: 'less_than_number',
      label: t('Less Than #'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', '<']],
    },
    less_than_or_equal_to: {
      value: 'less_than_or_equal_to',
      label: t('Less Than Or Equal To'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', '<=']],
    },
    month_equals: {
      value: 'month_equals',
      label: t('Month Equals'),
      next: [{ step: 'value', unset: true }, '__END__'],
      ops: [['condition', 'month_equals']],
    },
    more_than: {
      value: 'more_than',
      label: t('More Than'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', '>']],
    },
    more_than_number: {
      value: 'more_than_number',
      label: t('More Than #'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', '>']],
    },
    more_than_or_equal_to: {
      value: 'more_than_or_equal_to',
      label: t('More Than Or Equal To'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', '>=']],
    },
    none: {
      value: 'none',
      label: t('None'),
      next: ['__END__'],
      ops: [
        ['condition', 'is_blank'],
        ['value', true],
      ],
    },
    one_or_more: {
      value: 'one_or_more',
      label: t('One Or More'),
      next: ['__END__'],
      ops: [
        ['condition', '>='],
        ['value', 1],
      ],
    },
    starts_with: {
      value: 'starts_with',
      label: t('Starts With'),
      next: [{ step: 'value', unset: true }],
      ops: [['condition', 'starts_with']],
    },
  } as const;
};

export const getMetadata = (metadata: any, t = (x: string) => x) => {
  const conditions = getConditions(t);

  return {
    initial_steps: [['field_id']],
    order: {
      field_id: 1,
      condition: 2,
      value: 3,
    },
    steps: {
      field_id: {
        input_type: 'choices',
        placeholder: t('Choose Field'),
        width: 250,
        url: ['xf_join', '/activities/', '$activity_id', '/fields'],
        method: 'GET',
        option_paths: {
          value: [],
          label: ['display_name'],
        },
        value_id_path: ['id'],
        option_filter: [
          ['xf_eq', ['is_hidden'], false],
          ['xf_not_eq', ['field_type'], 'wysiwyg'],
        ],
        next: [{ step: 'condition', unset: true }, '__END__'],
        ops: [
          ['type', 'activity_field'],
          ['activity_field_id', ['$field_id', 'id']],
        ],
        save_value: {
          url: [
            'xf_join',
            '/activities/',
            '$activity_id',
            '/fields/',
            ['$field_id', 'id'],
          ],
          method: 'GET',
          error_response: {
            field_type: [
              ['$field_id', 'custom_object_field', 'field_type'],
              ['$field_id', 'field_type'],
            ],
          },
          error_message: {
            404: t('The field has been deleted'),
          },
        },
      },
      condition: {
        input_type: 'choices',
        placeholder: t('Choose Condition'),
        width: 180,
        options: [
          ['@__options_lookup', '@__field_name'],
          ['@__options_lookup', '@__field_type'],
        ],
        __field_type: [
          '$field_id',
          [['custom_object_field', 'field_type'], ['field_type']],
        ],
        __field_name: [
          '$field_id',
          [['custom_object_field', 'name'], ['name']],
        ],
        __options_lookup: {
          checkbox: [conditions.is_checked, conditions.is_not_checked],
          checkboxes: [
            conditions.has,
            conditions.has_not,
            conditions.has_any,
            conditions.has_not_any,
            conditions.is_blank,
            conditions.is_not_blank,
          ],
          created: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.earlier_than,
            conditions.earlier_than_or_on,
            conditions.later_than,
            conditions.later_than_or_on,
            conditions.between,
            conditions.month_equals,
          ],
          date: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.earlier_than,
            conditions.earlier_than_or_on,
            conditions.later_than,
            conditions.later_than_or_on,
            conditions.between,
            conditions.month_equals,
            conditions.is_blank,
            conditions.is_not_blank,
          ],
          datetime: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.earlier_than,
            conditions.earlier_than_or_on,
            conditions.later_than,
            conditions.later_than_or_on,
            conditions.between,
            conditions.month_equals,
            conditions.is_blank,
            conditions.is_not_blank,
          ],
          decimal: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.more_than,
            conditions.more_than_or_equal_to,
            conditions.less_than,
            conditions.less_than_or_equal_to,
            conditions.between,
            conditions.is_blank,
            conditions.is_not_blank,
          ],
          dropdown: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.is_any_of,
            conditions.is_none_of,
            conditions.is_blank,
            conditions.is_not_blank,
          ],
          dynamictags: [
            conditions.has,
            conditions.has_not,
            conditions.has_any,
            conditions.has_not_any,
            conditions.is_blank,
            conditions.is_not_blank,
          ],
          email: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.contains,
            conditions.does_not_contain,
            conditions.starts_with,
            conditions.ends_with,
            conditions.does_not_start_with,
            conditions.does_not_end_with,
            conditions.is_blank,
            conditions.is_not_blank,
          ],
          email_status: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.is_any_of,
            conditions.is_none_of,
          ],
          files: [
            conditions.none,
            conditions.one_or_more,
            conditions.more_than_number,
            conditions.less_than_number,
          ],
          integer: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.more_than,
            conditions.more_than_or_equal_to,
            conditions.less_than,
            conditions.less_than_or_equal_to,
            conditions.between,
            conditions.is_blank,
            conditions.is_not_blank,
          ],
          longtext: [conditions.is_blank, conditions.is_not_blank],
          money: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.more_than,
            conditions.more_than_or_equal_to,
            conditions.less_than,
            conditions.less_than_or_equal_to,
            conditions.between,
            conditions.is_blank,
            conditions.is_not_blank,
          ],
          owner: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.is_any_of,
            conditions.is_none_of,
            conditions.is_me,
          ],
          percentage_chance_to_close: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.more_than,
            conditions.more_than_or_equal_to,
            conditions.less_than,
            conditions.less_than_or_equal_to,
            conditions.between,
          ],
          phonenumber: [
            conditions.equals,
            conditions.contains,
            conditions.is_blank,
            conditions.is_not_blank,
          ],
          radio: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.is_any_of,
            conditions.is_none_of,
            conditions.is_blank,
            conditions.is_not_blank,
          ],
          rating: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.more_than,
            conditions.more_than_or_equal_to,
            conditions.less_than,
            conditions.less_than_or_equal_to,
            conditions.is_any_of,
            conditions.is_none_of,
            conditions.is_blank,
            conditions.is_not_blank,
          ],
          relationship: [
            conditions.is, // this condition value differs from filters, which is "contains"
            conditions.is_not, // this condition value differs from filters, which is "!contains"
            conditions.is_any_of,
            conditions.is_none_of,
            conditions.is_blank,
            conditions.is_not_blank,
          ],
          stage: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.is_any_of,
            conditions.is_none_of,
          ],
          status: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.is_any_of,
            conditions.is_none_of,
            conditions.is_blank,
            conditions.is_not_blank,
          ],
          team_selector: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.is_any_of,
            conditions.is_none_of,
            conditions.is_me,
            conditions.is_blank,
            conditions.is_not_blank,
          ],
          text: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.contains,
            conditions.does_not_contain,
            conditions.starts_with,
            conditions.ends_with,
            conditions.does_not_start_with,
            conditions.does_not_end_with,
            conditions.is_blank,
            conditions.is_not_blank,
          ],
          yesnomaybe: [
            conditions.equals,
            conditions.does_not_equal,
            conditions.is_any_of,
            conditions.is_none_of,
            conditions.is_blank,
            conditions.is_not_blank,
          ],
        },
      },
      value: {
        input_type: [
          ['@__input_types', '@__field_name', '$condition'],
          ['@__input_types', '@__field_name'],
          ['@__input_types', '@__field_type', '$condition'],
          ['@__input_types', '@__field_type', 'default'],
          ['@__input_types', '@__field_type'],
          'choices',
        ],
        width: [
          ['@__widths', '@__field_type', '$condition'],
          ['@__widths', '@__field_type', 'default'],
          ['@__widths', '@__field_name'],
          ['@__widths', '@__field_type'],
          200,
        ],
        paginated: [
          'xf_some',
          ['relationship', 'team_selector'],
          '@__field_type',
        ],
        placeholder: [
          ['@__placeholders', '@__field_name', '$condition'],
          ['@__placeholders', '@__field_name', 'default'],
          ['@__placeholders', '@__field_type', '$condition'],
          [
            '@__placeholders',
            '@__field_type',
            ['$field_id', 'relation', 'fetch_url'],
            '$condition',
          ],
          ['@__placeholders', '@__field_type', 'default', '$condition'],
          ['@__placeholders', '@__field_type', 'default'],
          ['@__placeholders', '@__field_name'],
          ['@__placeholders', '@__field_type'],
          t('Enter Value'),
        ],
        value_id_path: [
          ['@__options_lookup', '@__field_type', 'value_id_path'],
          ['id'],
        ],
        options: [
          [
            '@__options_lookup',
            '@__field_type',
            'options',
            ['$field_id', 'rating', 'max_value'],
          ],
          ['@__options_lookup', '@__field_name', 'options'],
          ['@__options_lookup', '@__field_type', 'options'],
          ['$field_id', 'options'],
        ],
        option_paths: [
          ['@__options_lookup', '@__field_type', 'paths'],
          {
            value: [],
            label: ['name'],
          },
        ],
        url: ['@__url_lookup', '@__field_type', 'url'],
        method: ['@__url_lookup', '@__field_type', 'method'],
        params: ['@__url_lookup', '@__field_type', 'params'],
        result_path: ['@__url_lookup', '@__field_type', 'result_path'],
        ops: [
          ['@__ops_lookup', '@__field_type', '@input_type'],
          ['@__ops_lookup', '@__field_type'],
          ['@__ops_lookup', '$condition'],
          ['@__ops_lookup', '@input_type'],
          [['value', '$value']],
        ],
        save_value: [
          ['@__save_value_lookup', '@__field_name', '@input_type'],
          ['@__save_value_lookup', '@__field_type', '@input_type'],
          ['@__save_value_lookup', '@__field_name'],
          ['@__save_value_lookup', '@__field_type'],
          '$value',
        ],
        __field_type: [
          '$field_id',
          [['custom_object_field', 'field_type'], ['field_type']],
        ],
        __field_name: [
          '$field_id',
          [['custom_object_field', 'name'], ['name']],
        ],
        __month_options: [
          { value: 1, label: t('January') },
          { value: 2, label: t('February') },
          { value: 3, label: t('March') },
          { value: 4, label: t('April') },
          { value: 5, label: t('May') },
          { value: 6, label: t('June') },
          { value: 7, label: t('July') },
          { value: 8, label: t('August') },
          { value: 9, label: t('September') },
          { value: 10, label: t('October') },
          { value: 11, label: t('November') },
          { value: 12, label: t('December') },
        ],
        __input_types: metadata.field_data.input_types,
        __placeholders: metadata.field_data.placeholders,
        __widths: metadata.field_data.widths,
        __url_lookup: {
          dynamictags: {
            url: [
              'xf_join',
              '/activities/',
              '$activity_id',
              '/fields/',
              ['$field_id', 'id'],
              '/options',
            ],
            method: 'GET',
            params: {
              ordering: 'name',
              page_size: 10000,
            },
          },
          relationship: {
            url: [
              'xf_join',
              '/records/',
              ['$field_id', 'relation', 'related_object'],
              '/typeahead-search',
            ],
            method: 'GET',
            params: {
              page_size: 100,
            },
            result_path: ['results'],
          },
          team_selector: {
            url: '/team/typeahead',
            method: 'GET',
            params: { ordering: 'display_name', page_size: 20 },
            result_path: ['results'],
            paginated: true,
          },
        },
        __options_lookup: {
          // Tags and titles return their options on the field, regular dynamictags do not. For simplicity,
          // we'll ignore the options on the field and let the url/method/params properties fetch the values.
          // The values here ensure the ['$field_id', 'options'] reference in "options" above does not return
          // any values. Without this you'll see duplicates
          tags: { options: null },
          titles: { options: null },
          date: {
            options: '@__month_options',
            paths: null,
          },
          datetime: {
            options: '@__month_options',
            paths: null,
          },
          dropdown: {
            options: ['$field_id', 'options'],
            paths: {
              value: [],
              label: ['name'],
              order: ['order'],
            },
          },
          rating: {
            options: {
              5: ['1', '2', '3', '4', '5'],
              10: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
            },
            value_id_path: null,
            paths: {
              value: [],
              label: [],
            },
          },
          relationship: {
            paths: {
              value: [],
              label: ['display_name'],
            },
          },
          status: {
            options: ['$field_id', 'options'],
            paths: {
              value: [],
              label: ['name'],
              order: ['order'],
              color: ['meta', 'color'],
            },
          },
          team_selector: {
            paths: {
              value: [],
              label: ['display_name'],
            },
          },
        },
        __ops_lookup: {
          // @__input_type
          choices: [['value', ['xf_pick', '$value', ['id']]]],
          multiselect: [
            ['value', ['xf_map', '$value', ['xf_pick', '$', ['id']]]],
          ],
          multiselect_one: [
            ['value', ['xf_map', '$value', ['xf_pick', '$', ['id']]]],
          ],
          // @__field_type
          date: [['value', '$value']],
          datetime: [['value', '$value']],
          email: [['value', '$value']],
          phonenumber: [['value', '$value']],
          rating: [['value', '$value']],
          status: {
            // @input_type
            status: [['value', ['xf_pick', '$value', ['id']]]],
            multiselect: [
              ['value', ['xf_map', '$value', ['xf_pick', '$', ['id']]]],
            ],
          },
          text: [['value', '$value']],
          // $condition
          // contains & does_not_contain here are meant for relationship fields, not text/email/phonenumber (which is why those field types are listed above)
          contains: [['value', ['xf_concat', ['xf_pick', '$value', ['id']]]]],
          does_not_contain: [
            ['value', ['xf_concat', ['xf_pick', '$value', ['id']]]],
          ],
        },
        __save_value_lookup: {
          stage: {
            choices: {
              method: 'POST',
              url: '/records/display-names',
              body: {
                option_ids: ['xf_concat', ['xf_pick', '$value', ['id']]],
              },
              result_path: [],
              result_transform: [
                'xf_hoist',
                [
                  'xf_concat',
                  [
                    'xf_map',
                    '@result_path/not_found/options',
                    {
                      id: ['xf_pick', '$', ['id']],
                      name: [
                        'xf_join',
                        '[Deleted] ',
                        ['xf_pick', '$', ['display_name']],
                      ],
                      not_found: true,
                      error_message: t('The stage has been deleted'),
                    },
                  ],
                  [
                    'xf_map',
                    '@result_path/results/options',
                    {
                      id: ['xf_pick', '$', ['id']],
                      name: ['xf_pick', '$', ['display_name']],
                    },
                  ],
                ],
              ],
            },
            multiselect: metadata.resources.stage_field_multiple,
          },
          dynamictags: {
            method: 'POST',
            url: '/records/display-names',
            body: {
              activity_tag_ids: ['xf_map', '$value', ['xf_pick', '$', ['id']]],
            },
            result_path: [],
            result_transform: [
              'xf_concat',
              [
                'xf_map',
                '@result_path/not_found/activity_tags',
                {
                  id: ['xf_pick', '$', ['id']],
                  name: [
                    'xf_join',
                    '[Deleted] ',
                    ['xf_pick', '$', ['display_name']],
                  ],
                  not_found: true,
                  error_message: t('The option has been deleted'),
                },
              ],
              [
                'xf_map',
                '@result_path/results/activity_tags',
                {
                  id: ['xf_pick', '$', ['id']],
                  name: ['xf_pick', '$', ['display_name']],
                },
              ],
            ],
          },
          relationship: {
            multiselect: {
              method: 'POST',
              url: '/records/display-names',
              body: {
                record_ids: ['xf_map', '$value', ['xf_pick', '$', ['id']]],
              },
              result_path: [],
              result_transform: [
                'xf_concat',
                [
                  'xf_map',
                  '@result_path/not_found/records',
                  {
                    id: ['xf_pick', '$', ['id']],
                    display_name: [
                      'xf_join',
                      '[Deleted] ',
                      ['xf_pick', '$', ['display_name']],
                    ],
                    not_found: true,
                    error_message: t('The record has been deleted'),
                  },
                ],
                [
                  'xf_map',
                  '@result_path/results/records',
                  {
                    id: ['xf_pick', '$', ['id']],
                    display_name: ['xf_pick', '$', ['display_name']],
                  },
                ],
              ],
            },
            choices: {
              method: 'POST',
              url: '/records/display-names',
              body: {
                record_ids: ['xf_concat', ['xf_pick', '$value', ['id']]],
              },
              result_path: [],
              result_transform: [
                'xf_hoist',
                [
                  'xf_concat',
                  [
                    'xf_map',
                    '@result_path/not_found/records',
                    {
                      id: ['xf_pick', '$', ['id']],
                      display_name: [
                        'xf_join',
                        '[Deleted] ',
                        ['xf_pick', '$', ['display_name']],
                      ],
                      not_found: true,
                      error_message: t('The record has been deleted'),
                    },
                  ],
                  [
                    'xf_map',
                    '@result_path/results/records',
                    {
                      id: ['xf_pick', '$', ['id']],
                      display_name: ['xf_pick', '$', ['display_name']],
                    },
                  ],
                ],
              ],
            },
          },
          checkboxes: multiple_field_options_resource(t),
          dropdown: {
            choices: individual_field_option_resource(t),
            multiselect: multiple_field_options_resource(t),
          },
          radio: {
            choices: individual_field_option_resource(t),
            multiselect: multiple_field_options_resource(t),
          },
          status: {
            multiselect: multiple_field_options_resource(t),
            status: individual_field_option_resource(t),
          },
          team_selector: {
            choices: metadata.resources.team_member_individual,
            multiselect: metadata.resources.team_member_multiple,
          },
          yesnomaybe: {
            choices: individual_field_option_resource(t),
            multiselect: multiple_field_options_resource(t),
          },
        },
      },
    },
  } satisfies FilterMetadata;
};
